import { useRef, useEffect, useState } from "react";
import "./App.css";
import SampleGalleryTabs from "./SampleGalleryTabs";
import ContactPanel from "./ContactPanel";
import DoubleDownChevron from "./DoubleDownChevron";
import { Box } from "@mui/material";
import LicenseInfo from "./LicenseInfo";
import LoadingSpinner from './SharedComponents/LoadingSpinner';
import { Helmet } from 'react-helmet';

const BACKGROUNDS = {
  landscape: "/home-background/home_background_landscape.png",
  portrait: "/home-background/home_background_portrait.png",
  square: "/home-background/home_background_square.png",
};

function getAspectType(width, height) {
  const ratio = width / height;
  if (ratio > 1.2) return "landscape";
  if (ratio < 0.8) return "portrait";
  return "square";
}

const Home = () => {
  const foregroundBoxRef = useRef(null);
  const [backgroundUrl, setBackgroundUrl] = useState(BACKGROUNDS.landscape);
  const [isBgLoaded, setIsBgLoaded] = useState(false);

  useEffect(() => {
    function updateBackground() {
      const aspectType = getAspectType(window.innerWidth, window.innerHeight);
      setBackgroundUrl(BACKGROUNDS[aspectType]);
      setIsBgLoaded(false);
      const img = new window.Image();
      img.src = BACKGROUNDS[aspectType];
      img.onload = () => setIsBgLoaded(true);
      img.onerror = () => setIsBgLoaded(true);
    }
    updateBackground();
    window.addEventListener("resize", updateBackground);
    return () => window.removeEventListener("resize", updateBackground);
  }, []);

  const scrollToTop = () => {
    const element = foregroundBoxRef.current;
    if (element) {
      const targetPosition = element.scrollTop + element.clientHeight * 0.9;
      element.scrollTo({ top: targetPosition, behavior: "smooth" });
    }
  };

  if (!isBgLoaded) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="100vw"
        height="100vh"
        sx={{ backgroundColor: "white" }}
      >
        <LoadingSpinner />
      </Box>
    );
  }

  return (
    <Box position="absolute" top="0px" width="100vw" height="100vh">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ConstructionCompany",
            "name": "Emduben Construction Services LLC",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Brainerd",
              "addressRegion": "MN",
              "postalCode": "56401",
              "addressCountry": "US"
            },
            "serviceType": [
              "Basement Finishing",
              "Finished Basements",
              "Window Replacement",
              "Window Installation",
              "Deck Installation",
              "Custom Decks",
              "Home Additions",
              "Room Additions",
              "New Construction",
              "New Home Construction",
              "Framing Services",
              "Framing Contractors",
              "Kitchen Remodels",
              "Kitchen Renovation",
              "Updated Kitchens",
              "Bathroom Remodels",
              "Bathroom Renovation",
              "Updated Bathrooms",
              "Home Remodels",
              "Remodeling",
              "Home Renovation",
              "Home Renovations",
              "General Contracting",
              "General Contractor Services",
              "Pole Barn Construction",
              "Pole Barns",
              "Porch Construction",
              "Covered Porches",
              "Shed Construction",
              "Backyard Sheds",
              "Garage Construction",
              "Detached Garages",
              "Door Installation",
              "Interior Door Installation",
              "Exterior Door Installation",
              "Home Builder",
              "Custom Home Builder",
              "Residential Construction",
              "Home Improvement",
              "House Renovation",
              "Remodeling Services",
              "Additions",
              "Home Extensions"
            ],
            "areaServed": [
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Aitkin",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Crosby",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Backus",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Baxter",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Pillager",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Pequot Lakes",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Nisswa",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Breezy Point",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Crosslake",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Merrifield",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Pine River",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Little Falls",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Pierz",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Garrison",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Onamia",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Pequot Lakes",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Randall",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Crosslake",
                "addressRegion": "MN"
              }
            },
            {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Emily",
                "addressRegion": "MN"
              }
            }
          ],
          "telephone": "+1-612-401-4794",
          "url": "https://emduben-construction-services.com"
          })}
        </script>
        <meta name="description" content="Emmduben Construction Services LLC offers basement finishing, window replacement and installation, deck installation, home additions, new construction and framing servcies, kitchen remodels, bathroom remodels, home remodels, or other remodelling services. Servies are offered in Brainerd, Baxter, Backus, Pine River, Pequot Lakes, Nisswa, Breezy Point, Crosslake, Merrifield, Crosby, Aitkin, Emily, Garrison, Fort Ripley, Randall, Little Falls, Pillager, Motley, and Pierz, MN" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Background Box */}
      <Box
        position="fixed"
        top={64}
        left={0}
        width="100vw"
        height="90vh"
        zIndex={0}
        sx={{
          backgroundImage: `url('${backgroundUrl}')`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          transition: "background-image 0.5s ease-in-out",
        }}
      />

      {/* Foreground Box */}
      <Box
        ref={foregroundBoxRef}
        overflow="auto"
        width="100%"
        height="100%"
        zIndex={1}
        position="absolute"
        top={0}
      >
        <Box width="100%" height="85%" sx={{ backgroundColor: "transparent" }} />
        <Box width="100%" height="15%" display="flex" justifyContent="center">
          <DoubleDownChevron onClick={scrollToTop} />
        </Box>

        <Box
          width="100%"
          backgroundColor="white"
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <div className="info-panel">
            Emduben Construction is a licensed and insured contracting business serving the Brainerd Lakes area.
            <br /><br />
            With a keen eye for detail and a perfectionist's mindset, Emduben Construction is committed to delivering quality craftsmanship.
            <br /><br />
            We perform all major residential construction services, including roofing, siding, decks, patios, basements, additions, framing, and more. Call or email us today to discuss your project!
          </div>

          <Box
            className="separator"
            sx={{
              height: '1px',
              backgroundColor: '#ccc',
              margin: '16px auto',
              width: '80vw',
              display: { xs: 'none', sm: 'block' },
            }}
          />

          <SampleGalleryTabs />

          <Box
            className="separator"
            sx={{
              height: '1px',
              backgroundColor: '#ccc',
              margin: '16px auto',
              width: '80vw',
              display: { xs: 'none', sm: 'block' },
            }}
          />

          <ContactPanel />
          <Box sx={{ height: 50 }} /> {/* Add whitespace below ContactPanel */}
          <LicenseInfo />
        </Box>
      </Box>
    </Box>
  );
};

export default Home;